import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

// Message type for chat
export interface ChatMessage {
  from: "user" | "bot";
  text: string;
  timestamp?: string;
}

// Chat session state
export interface ChatSession {
  sessionId: string;
  messages: ChatMessage[];
  createdAt: string;
  updatedAt: string;
}

// MLOps Agent state
export interface MlopsAgentState {
  currentSessionId: string | null;
  chatSessions: Record<string, ChatSession>; // sessionId -> ChatSession
}

const initialState: MlopsAgentState = {
  currentSessionId: null,
  chatSessions: {},
};

// Helper to generate session ID
const generateSessionId = (): string => {
  const randomBytes = new Uint8Array(8);
  globalThis.crypto.getRandomValues(randomBytes);
  const randomSuffix = Array.from(randomBytes, (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");
  return `session_${Date.now()}_${randomSuffix}`;
};

// Helper to get current timestamp
const getTimestamp = (): string => new Date().toISOString();

// Helper to check if a session is older than specified days
const isOlderThanDays = (dateString: string, days: number): boolean => {
  const sessionDate = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - sessionDate.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays > days;
};

// Max number of chat sessions to retain in store
const MAX_SESSIONS = 10;
// Max number of messages per session
const MAX_MESSAGES_PER_SESSION = 100;

export const mlopsAgentSlice = createSlice({
  name: "mlopsAgent",
  initialState,
  reducers: {
    // Cleanup old sessions (older than 1 day) and enforce max session limit
    cleanupOldSessions: (state) => {
      const sessionsToDelete: string[] = [];
      
      Object.entries(state.chatSessions).forEach(([sessionId, session]) => {
        // Check if session is older than 1 day and not the current session
        if (sessionId !== state.currentSessionId && isOlderThanDays(session.updatedAt, 1)) {
          sessionsToDelete.push(sessionId);
        }
      });
      
      // Delete old sessions
      sessionsToDelete.forEach((sessionId) => {
        delete state.chatSessions[sessionId];
      });

      // Enforce max sessions limit — keep the most recent ones
      const remainingIds = Object.keys(state.chatSessions)
        .filter((id) => id !== state.currentSessionId)
        .sort((a, b) => {
          const aTime = new Date(state.chatSessions[a].updatedAt).getTime();
          const bTime = new Date(state.chatSessions[b].updatedAt).getTime();
          return bTime - aTime; // newest first
        });

      if (remainingIds.length >= MAX_SESSIONS) {
        const toRemove = remainingIds.slice(MAX_SESSIONS - 1); // keep room for current
        toRemove.forEach((id) => {
          delete state.chatSessions[id];
        });
      }
      
      // If current session was deleted (somehow), reset it
      if (state.currentSessionId && !state.chatSessions[state.currentSessionId]) {
        state.currentSessionId = null;
      }
    },

    // Initialize or get existing session
    initializeSession: (state) => {
      if (!state.currentSessionId || !state.chatSessions[state.currentSessionId]) {
        const sessionId = generateSessionId();
        state.currentSessionId = sessionId;
        state.chatSessions[sessionId] = {
          sessionId,
          messages: [
            {
              from: "bot",
              text: "How can I help you today?",
              timestamp: getTimestamp(),
            },
          ],
          createdAt: getTimestamp(),
          updatedAt: getTimestamp(),
        };
      } else {
        // Fix any incomplete streaming messages left by a page refresh.
        // A bot message with empty text means streaming was interrupted.
        const session = state.chatSessions[state.currentSessionId];
        session.messages = session.messages.map((msg) =>
          msg.from === "bot" && msg.text === ""
            ? { ...msg, text: "Something went wrong. Please try again." }
            : msg
        );
      }
    },

    // Add a message to current session
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      const sessionId = state.currentSessionId;
      if (sessionId && state.chatSessions[sessionId]) {
        const messages = state.chatSessions[sessionId].messages;
        // Trim oldest messages if limit exceeded (keep initial bot greeting)
        if (messages.length >= MAX_MESSAGES_PER_SESSION) {
          const trimCount = messages.length - MAX_MESSAGES_PER_SESSION + 2;
          state.chatSessions[sessionId].messages = [
            messages[0],
            ...messages.slice(trimCount + 1),
          ];
        }
        state.chatSessions[sessionId].messages.push({
          ...action.payload,
          timestamp: action.payload.timestamp || getTimestamp(),
        });
        state.chatSessions[sessionId].updatedAt = getTimestamp();
      }
    },

    // Update the last bot message (for streaming)
    updateLastBotMessage: (state, action: PayloadAction<string>) => {
      const sessionId = state.currentSessionId;
      if (sessionId && state.chatSessions[sessionId]) {
        const messages = state.chatSessions[sessionId].messages;
        const lastIndex = messages.length - 1;
        if (lastIndex >= 0 && messages[lastIndex].from === "bot") {
          messages[lastIndex].text += action.payload;
          state.chatSessions[sessionId].updatedAt = getTimestamp();
        }
      }
    },

    // Append chunk to specific message index
    appendToMessage: (
      state,
      action: PayloadAction<{ index: number; chunk: string }>
    ) => {
      const sessionId = state.currentSessionId;
      if (sessionId && state.chatSessions[sessionId]) {
        const { index, chunk } = action.payload;
        const messages = state.chatSessions[sessionId].messages;
        if (messages[index] && messages[index].from === "bot") {
          messages[index].text += chunk;
          state.chatSessions[sessionId].updatedAt = getTimestamp();
        }
      }
    },

    // Reset chat - create new session
    resetChat: (state) => {
      const sessionId = generateSessionId();
      state.currentSessionId = sessionId;
      state.chatSessions[sessionId] = {
        sessionId,
        messages: [
          {
            from: "bot",
            text: "How can I help you today?",
            timestamp: getTimestamp(),
          },
        ],
        createdAt: getTimestamp(),
        updatedAt: getTimestamp(),
      };
    },

    // Clear all chat history
    clearAllHistory: (state) => {
      state.currentSessionId = null;
      state.chatSessions = {};
    },

    // Set messages for current session (bulk update)
    setMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      const sessionId = state.currentSessionId;
      if (sessionId && state.chatSessions[sessionId]) {
        state.chatSessions[sessionId].messages = action.payload;
        state.chatSessions[sessionId].updatedAt = getTimestamp();
      }
    },
  },
});

export const {
  initializeSession,
  addMessage,
  updateLastBotMessage,
  appendToMessage,
  resetChat,
  clearAllHistory,
  setMessages,
  cleanupOldSessions,
} = mlopsAgentSlice.actions;

// Persist config for mlopsAgent
const persistConfig = {
  key: "mlopsAgent",
  storage,
  whitelist: ["currentSessionId", "chatSessions"], // Persist these fields
};

const persistedMlopsAgentReducer = persistReducer(
  persistConfig,
  mlopsAgentSlice.reducer
);

export default persistedMlopsAgentReducer;
